/**
 * Done. / ChiroCPD — AppLayout
 *
 * Wraps authenticated pages: TopNav + LockoutBanner (conditional) + <main> + BrandFooter.
 * Provides AuthProvider + CapStatusProvider + CopyFieldProvider to all children.
 * Main content is dimmed when subscription is expired (but not inert — user can still read).
 */

import type { ReactNode } from 'react';
import { CapStatusProvider } from '@/contexts/CapStatusContext';
import { CopyFieldProvider } from '@/contexts/CopyFieldContext';
import { TopNav } from '@/components/features/TopNav';
import { LockoutBanner } from '@/components/ui/LockoutBanner';
import { BrandFooter } from '@/components/ui/BrandFooter';
import { useAuth } from '@/hooks/useAuth';
import { isSubscriptionActive } from '@/lib/utils';
import { cx } from '@/lib/utils';

// ── Inner layout (needs auth context) ────────────────────────────────────────

interface InnerLayoutProps {
  children: ReactNode;
  currentRoute: string;
}

function InnerLayout({ children, currentRoute }: InnerLayoutProps): React.ReactElement {
  const { user, subscriptionStatus, loading } = useAuth();
  const expiresAt = user?.subscriptionExpiresAt ?? null;

  // Show nothing while session loads (avoid flash)
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <svg className="h-8 w-8 animate-spin text-[var(--color-done-500)]" fill="none" viewBox="0 0 24 24" aria-label="Loading…">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  const status = subscriptionStatus ?? 'inactive';
  const active = isSubscriptionActive(status, expiresAt);
  const displayName = user?.name ?? user?.email ?? 'User';

  return (
    <div className="flex min-h-screen flex-col bg-[var(--surface-subtle)]">
      {/* Lockout banner — shown when subscription is lapsed */}
      {!active && (
        <LockoutBanner resubscribeUrl="/" />
      )}

      {/* Top navigation */}
      <TopNav
        subscriptionStatus={status}
        subscriptionExpiresAt={expiresAt}
        user={{ name: displayName, isAdmin: user?.isAdmin ?? false }}
        currentRoute={currentRoute}
      />

      {/* Main content */}
      <main
        id="main-content"
        className={cx(
          'flex-1 mx-auto w-full max-w-5xl px-4 py-8',
          !active && 'opacity-60 pointer-events-none select-none',
        )}
        aria-label="Main content"
        tabIndex={-1}
      >
        {children}
      </main>

      <BrandFooter />
    </div>
  );
}

// ── Exported layout (wraps with providers) ────────────────────────────────────

interface AppLayoutProps {
  children: ReactNode;
  currentRoute: string;
}

export function AppLayout({ children, currentRoute }: AppLayoutProps): React.ReactElement {
  return (
    <CapStatusProvider>
      <CopyFieldProvider>
        <InnerLayout currentRoute={currentRoute}>
          {children}
        </InnerLayout>
      </CopyFieldProvider>
    </CapStatusProvider>
  );
}
